import * as T from 'three';
import { makeNoise } from './noise';

/**
 * A terrain material layer: RGB = albedo with baked AO, A = roughness, plus an OpenGL
 * normal map. Both use flipY = false so their texel rows agree.
 */
export interface Layer { albedo: T.Texture; normal: T.Texture }

const BASE = `${import.meta.env.BASE_URL}assets/env/textures/`;
const cache = new Map<string, Promise<Layer | null>>();

async function image(url: string): Promise<HTMLImageElement | ImageBitmap> {
  const response = await fetch(url); if (!response.ok) throw new Error(`${url}: ${response.status}`);
  const blob = await response.blob();
  if ('createImageBitmap' in window) return createImageBitmap(blob, { imageOrientation: 'none', premultiplyAlpha: 'none', colorSpaceConversion: 'none' } as ImageBitmapOptions);
  const img = new Image(); img.src = URL.createObjectURL(blob); await img.decode(); return img;
}
function pixels(source: HTMLImageElement | ImageBitmap) {
  const canvas = document.createElement('canvas'); canvas.width = source.width; canvas.height = source.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!; ctx.drawImage(source, 0, 0);
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}
function finish(texture: T.Texture, color: boolean, anisotropy: number) {
  texture.wrapS = texture.wrapT = T.RepeatWrapping; texture.flipY = false;
  texture.magFilter = T.LinearFilter; texture.minFilter = T.LinearMipmapLinearFilter; texture.generateMipmaps = true;
  texture.anisotropy = anisotropy; texture.colorSpace = color ? T.SRGBColorSpace : T.NoColorSpace; texture.needsUpdate = true;
  return texture;
}

/** Loads `<id>/albedo|normal|arm.webp`; resolves null when the set is missing. */
export function loadLayer(id: string, anisotropy: number): Promise<Layer | null> {
  const key = `${id}@${anisotropy}`;
  if (!cache.has(key)) cache.set(key, (async () => {
    try {
      const [albedo, normal, arm] = await Promise.all(['albedo', 'normal', 'arm'].map(name => image(`${BASE}${id}/${name}.webp`)));
      const a = pixels(albedo), r = pixels(arm.width === albedo.width ? arm : albedo), n = pixels(normal);
      const packed = new Uint8Array(a.width * a.height * 4);
      for (let i = 0; i < packed.length; i += 4) {
        const ao = arm.width === albedo.width ? .55 + .45 * r.data[i] / 255 : 1;
        packed[i] = a.data[i] * ao; packed[i + 1] = a.data[i + 1] * ao; packed[i + 2] = a.data[i + 2] * ao;
        packed[i + 3] = arm.width === albedo.width ? r.data[i + 1] : 220;
      }
      const albedoTexture = finish(new T.DataTexture(packed, a.width, a.height, T.RGBAFormat), true, anisotropy);
      const normalTexture = finish(new T.DataTexture(new Uint8Array(n.data.buffer.slice(0)), n.width, n.height, T.RGBAFormat), false, anisotropy);
      if ('close' in albedo) { albedo.close(); (normal as ImageBitmap).close(); (arm as ImageBitmap).close(); }
      return { albedo: albedoTexture, normal: normalTexture };
    } catch { return null; }
  })());
  return cache.get(key)!;
}

/** Offline-safe stand-in: noisy albedo/roughness and a matching height-derived normal map. */
export function proceduralLayer(color: string, roughness: number, seed: number, grain = 1, size = 256): Layer {
  const n = makeNoise(seed), base = new T.Color(color), heights = new Float32Array(size * size);
  const tile = (x: number, z: number, p: number) => {
    const ix = Math.floor(x), iz = Math.floor(z), fx = x - ix, fz = z - iz, ux = fx * fx * (3 - 2 * fx), uz = fz * fz * (3 - 2 * fz);
    const w = (v: number) => ((v % p) + p) % p;
    const a = n.hash(w(ix), w(iz)), b = n.hash(w(ix + 1), w(iz)), c = n.hash(w(ix), w(iz + 1)), d = n.hash(w(ix + 1), w(iz + 1));
    return a + (b - a) * ux + (c - a) * uz + (a - b - c + d) * ux * uz;
  };
  for (let z = 0; z < size; z++) for (let x = 0; x < size; x++) {
    let h = 0, amp = .5;
    for (let o = 0, p = 8; o < 5; o++, p *= 2, amp *= .5) h += tile(x * p / size, z * p / size, p) * amp;
    heights[z * size + x] = h + (n.hash(x, z) - .5) * .18 * grain;
  }
  const albedo = new Uint8Array(size * size * 4), normal = new Uint8Array(size * size * 4);
  for (let z = 0; z < size; z++) for (let x = 0; x < size; x++) {
    const i = z * size + x, h = heights[i], k = .72 + h * .56;
    albedo[i * 4] = Math.min(255, base.r * k * 255 * 1.0); albedo[i * 4 + 1] = Math.min(255, base.g * k * 255); albedo[i * 4 + 2] = Math.min(255, base.b * k * 255);
    albedo[i * 4 + 3] = Math.max(0, Math.min(255, (roughness + (.5 - h) * .12) * 255));
    const hx = heights[z * size + (x + 1) % size] - heights[z * size + (x + size - 1) % size];
    const hz = heights[((z + 1) % size) * size + x] - heights[((z + size - 1) % size) * size + x];
    const v = new T.Vector3(-hx * 6 * grain, hz * 6 * grain, 1).normalize();
    normal[i * 4] = (v.x * .5 + .5) * 255; normal[i * 4 + 1] = (v.y * .5 + .5) * 255; normal[i * 4 + 2] = (v.z * .5 + .5) * 255; normal[i * 4 + 3] = 255;
  }
  // Base colour is given in sRGB; store it encoded so the sRGB sampler decodes it back.
  for (let i = 0; i < albedo.length; i += 4) for (let c = 0; c < 3; c++) {
    const lin = albedo[i + c] / 255; albedo[i + c] = Math.round((lin <= .0031308 ? lin * 12.92 : 1.055 * Math.pow(lin, 1 / 2.4) - .055) * 255);
  }
  return { albedo: finish(new T.DataTexture(albedo, size, size, T.RGBAFormat), true, 4), normal: finish(new T.DataTexture(normal, size, size, T.RGBAFormat), false, 4) };
}
